import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DashboardWidgetConfig } from 'src/types/dashboard';

export type Filter = { [key: string]: string };

export interface WidgetFilterConfig {
  filterName: string;
  values: { label: string; value: string }[];
  default?: string;
}

interface WidgetFilterConfigs {
  [key: string]: WidgetFilterConfig;
}

interface StandardRawFilter {
  id: number | string;
  name: string;
  default?: boolean;
}

const parseStandardFilter = (
  filterName: string,
  filterConfig: readonly StandardRawFilter[],
) => {
  let defaultValue;
  return {
    filterName: filterName,
    values: filterConfig.map(({ id, name, default: isDefault }) => {
      if (isDefault) {
        defaultValue = String(id);
      }
      return {
        label: name,
        value: String(id),
      };
    }),
    default: defaultValue,
  };
};

const getDefaultFilter = (filterConfig: WidgetFilterConfigs) =>
  Object.values(filterConfig).reduce(
    (acc, { filterName, values, default: defaultValue }) => ({
      ...acc,
      // fallback to empty string so MUI knows the component is controlled
      [filterName]: defaultValue
        ? defaultValue
        : values && values.length
          ? values[0].value
          : '',
    }),
    {},
  );

const useWidgetFilterConfig = (widgetConfig: DashboardWidgetConfig) => {
  const { t } = useTranslation(['followUp']);
  const filterConfig = useMemo(() => {
    const widgetFilter = widgetConfig.widget_filter_set;
    const result: WidgetFilterConfigs = {};

    if (!widgetFilter) return {};

    if (widgetFilter.variables && widgetFilter.variables.length) {
      Object.assign(result, {
        variableFilterConfig: parseStandardFilter(
          'variable',
          widgetFilter.variables,
        ),
      });
    }

    if (widgetFilter.time_steps && widgetFilter.time_steps.length) {
      const translatedTimeSteps = widgetFilter.time_steps.map(
        ({ id, default: isDefault }) => ({
          default: isDefault,
          name: t(`statistics.npsTrendAnalysisWidget.timeStep.${id}`),
          id: id,
        }),
      );
      Object.assign(result, {
        timeStepFilterConfig: parseStandardFilter(
          'time_step',
          translatedTimeSteps,
        ),
      });
    }

    return result;
  }, [widgetConfig.widget_filter_set]);

  const [filters, setFiltersState] = useState<Filter>(
    getDefaultFilter(filterConfig),
  );
  const setFilters = (filters: Filter) =>
    setFiltersState((prev) => ({ ...prev, ...filters }));

  return { filterConfig, filters, setFilters };
};

export default useWidgetFilterConfig;
